import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from "react";

//MUI Imports
import {Box, TextField, Typography, InputAdornment, Divider, Snackbar, Alert} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Stack } from '@mui/system';

//Other Imports
import { GetSubsetOfDataByPage } from '../../api';
import { PaginatedDataTable } from '../common/tables';

export const SubsetResultsPage = (props) => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [totalNumResults, setTotalNumResults] = useState(-1);
    const [page, setPage] = useState(0);
    const [query, setQuery] = useState({});
    const [columns, setColumns] = useState([]);
    const [selected, setSelected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [columnFilters, setColumnFilters] = useState({});
    const [currentPageSize, setCurrentPageSize] = useState(10);

    // Custom component to handle dual-color highlighting
    const DualHighlighter = ({ text, globalTerms, columnTerm }) => {
        if (!text) return "";

        const textStr = text.toString();

        // Build all matches with their types
        const matches = [];

        // Add column-specific matches (priority)
        if (columnTerm && columnTerm.trim()) {
            const regex = new RegExp(columnTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            let match;
            while ((match = regex.exec(textStr)) !== null) {
                matches.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    text: match[0],
                    type: 'column'
                });
            }
        }

        // Add global matches
        globalTerms.forEach(term => {
            if (!term || term.trim() === '') return;
            const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            let match;
            while ((match = regex.exec(textStr)) !== null) {
                matches.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    text: match[0],
                    type: 'global'
                });
            }
        });

        // Sort and remove overlaps, prioritizing column matches
        matches.sort((a, b) => a.start - b.start);
        const cleanMatches = [];
        for (let i = 0; i < matches.length; i++) {
            const current = matches[i];
            const overlapIndex = cleanMatches.findIndex(existing =>
                current.start < existing.end && current.end > existing.start
            );

            if (overlapIndex === -1) {
                cleanMatches.push(current);
            } else if (current.type === 'column' && cleanMatches[overlapIndex].type === 'global') {
                // Replace global with column match
                cleanMatches[overlapIndex] = current;
            }
        }

        // Sort final matches by position
        cleanMatches.sort((a, b) => a.start - b.start);

        // Build the highlighted text
        const parts = [];
        let lastEnd = 0;

        cleanMatches.forEach((match, index) => {
            // Add text before match
            if (match.start > lastEnd) {
                parts.push(textStr.slice(lastEnd, match.start));
            }

            // Add highlighted match
            parts.push(
                <span
                    key={index}
                    style={{
                        backgroundColor: match.type === 'column' ? '#e3f2fd' : '#fff3cd',
                        color: match.type === 'column' ? '#1976d2' : '#856404',
                        fontWeight: 'bold',
                        padding: '2px 4px',
                        borderRadius: '3px'
                    }}
                >
                    {match.text}
                </span>
            );

            lastEnd = match.end;
        });

        // Add remaining text
        if (lastEnd < textStr.length) {
            parts.push(textStr.slice(lastEnd));
        }

        return parts.length > 0 ? <span>{parts}</span> : text;
    };

    const highlight = (results, globalTerms, colFilters) => {
        results.map(row => {
            Object.keys(row).forEach(col => {
                if (col !== "record_id") {
                    if (!row[col]) {
                        row[col] = "";
                    } else if (typeof row[col] !== "string") {
                        row[col] = row[col].toString();
                    }

                    const text = row[col];

                    // Find the column filter with case-insensitive matching
                    // colFilters has keys with original case (e.g., "Country")
                    // but row keys are lowercase (e.g., "country")
                    const columnFilterKey = Object.keys(colFilters).find(
                        filterKey => filterKey.toLowerCase() === col.toLowerCase()
                    );
                    const columnTerm = columnFilterKey ? colFilters[columnFilterKey] : '';

                    // Only highlight if there are search terms
                    if (globalTerms.length > 0 || columnTerm.trim()) {
                        row[col] = <DualHighlighter text={text} globalTerms={globalTerms} columnTerm={columnTerm} />;
                    }
                }
            });
            return row;
        });
        setSearchResults(results);
    }

    const fetchSubset = (pageSize = currentPageSize) => {
        let _query = {
            simpleSearch: searchTerm,
            columnFilters: columnFilters
        };

        let demoV = JSON.parse(localStorage.getItem('democracy-viewer'));
        demoV.downloadData = _query;
        localStorage.setItem('democracy-viewer', JSON.stringify(demoV));

        setLoading(true);
        const globalTerms = searchTerm.split(" ").filter(t => t.trim().length > 0);
        GetSubsetOfDataByPage(demoV.dataset.table_name, _query, 1, pageSize).then(async (res) => {
            if (!res) {
                setSearchResults([]);
            } else {
                highlight(res.data, globalTerms, columnFilters);
                setTotalNumResults(res.count);
                setColumns(res.columns);
            }
            setPage(1);
        }).finally(() => setLoading(false));

        setQuery(_query);
    }

    const GetNewPage = async (selectedPage, pageSize = currentPageSize) => {
        setLoading(true);
        try {
            const globalTerms = searchTerm.split(" ").filter(t => t.trim().length > 0);
            const res = await GetSubsetOfDataByPage(props.dataset.table_name, query, selectedPage, pageSize);
            if (res) {
                setPage(selectedPage);
                highlight(res.data, globalTerms, columnFilters);
            }
        } catch (error) {
            console.error('Error fetching new page:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleColumnFilterChange = (columnName, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [columnName]: value
        }));
    };

    const handlePageSizeChange = (newPageSize) => {
        setCurrentPageSize(newPageSize);
        fetchSubset(newPageSize);
    };

    const handleKeyPress = event => {
        if (event.key === 'Enter') {
            fetchSubset();
        }
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    // Add debounced search effect for global search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (selected) {
                fetchSubset();
            }
        }, 500); // 500ms delay after the user stops typing

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    // Add debounced search effect for column filters
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (selected) {
                fetchSubset();
            }
        }, 500); // 500ms delay after the user stops typing

        return () => clearTimeout(delayDebounceFn);
    }, [columnFilters]);

    useEffect(() => {
        let demoV = JSON.parse(localStorage.getItem('democracy-viewer'));
        if (!demoV || !demoV.dataset) {
            navigate('/datasets/search');
            props.setNavigated(true);
            setSnackBarOpen(true);
        } else {
            fetchSubset();
            setSelected(true);
        }
    }, []);

    if (!selected) {
        return <></>;
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            backgroundColor: '#f8f9fa',
            py: 13,
            position: 'relative'
        }}>
            {/* Error Snackbar */}
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackBarOpen}
                autoHideDuration={6000}
                onClose={handleSnackBarClose}
            >
                <Alert onClose={handleSnackBarClose} severity="error" sx={{ width: '100%' }}>
                    You must choose a dataset first
                </Alert>
            </Snackbar>
            
            {/* Title Section */}
            <Box sx={{ 
                width: '100%', 
                textAlign: 'center', 
                mb: 4
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 1
                }}>
                    <Typography 
                        component="h1" 
                        variant="h3" 
                        sx={{ 
                            fontSize: '2.5rem', 
                            color: 'black',
                            fontWeight: 500
                        }}
                    >
                        {props.dataset.title}
                    </Typography>
                </Box>
            </Box>
            
            <Stack spacing={0}>
                {/* Search Section */}
                <Box sx={{ 
                    p: 4, 
                    backgroundColor: '#f8f9fa'
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3
                        }}
                    >
                        <TextField
                            sx={{
                                width: { xs: "100%", sm: "500px" },
                                backgroundColor: 'white'
                            }}
                            id="searchTerm"
                            placeholder="Search dataset content..."
                            variant="outlined"
                            value={searchTerm}
                            onChange={event => { setSearchTerm(event.target.value) }}
                            onKeyDown={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Search Instructions */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Box sx={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #856404',
                                    borderRadius: '3px'
                                }} />
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Global search highlights
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Box sx={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #1976d2',
                                    borderRadius: '3px'
                                }} />
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Column-specific highlights
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                <Divider />
                
                {/* Results Summary */}
                <Box sx={{ 
                    px: 4, 
                    py: 2, 
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        {loading ? 'Loading results...' : 
                         totalNumResults >= 0 ? `Showing ${searchResults.length} of ${totalNumResults} results` : 'Ready to search'}
                    </Typography>
                </Box>
                
                {/* Results Section */}
                <Box sx={{ p: 3 }}>
                    <PaginatedDataTable
                        searchResults={searchResults}
                        page={page}
                        GetNewPage={GetNewPage}
                        table_name={props.dataset.table_name}
                        downloadType="subset"
                        totalNumResults={totalNumResults}
                        pageLength={currentPageSize}
                        columns={columns}
                        extLoading={loading}
                        globalSearchTerm={searchTerm}
                        columnFilters={columnFilters}
                        onColumnFilterChange={handleColumnFilterChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </Box>
            </Stack>
        </Box>
    );
}